import React from 'react';
import { ReactComponent as GroupIcon } from '../../../assets/icons/group-icon.svg';
import { ReactComponent as ChevronRight } from '../../../assets/icons/chevron-right.svg';

interface Group {
  id: string;
  name: string;
}

interface GroupSelectorProps {
  selectedGroupId: string;
  setSelectedGroupId: (id: string) => void;
  isGroupListOpen: boolean;
  setIsGroupListOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isSettled: boolean;
  disabled: boolean;
  groups: Record<string, Group>;
  groupList: Group[];
  groupImageUrls: Record<string, string>;
}

const NO_GROUPS_MESSAGE = "No groups yet — create an asset group first.";
const PICK_GROUP_MESSAGE = "Select a group to add these assets to.";

const GroupSelector: React.FC<GroupSelectorProps> = ({
  selectedGroupId,
  setSelectedGroupId,
  isGroupListOpen,
  setIsGroupListOpen,
  isSettled,
  disabled,
  groups,
  groupList,
  groupImageUrls,
}) => (
  <div className="border-b border-eai-border pb-4">
    <button
      type="button"
      disabled={disabled || isSettled}
      onClick={() => !isSettled && setIsGroupListOpen((o) => !o)}
      className="flex items-center gap-1 mb-2 disabled:cursor-default"
    >
      <p className="text-xs font-semibold text-eai-ink-faint uppercase tracking-wide">
        {isSettled ? 'Added to Group' : 'Add to Group'}
      </p>
      {!isSettled && (
        <ChevronRight className={`w-3 h-3 flex-shrink-0 text-eai-ink-faint transition-transform ${isGroupListOpen ? 'rotate-90' : ''}`} />
      )}
    </button>

    {!isGroupListOpen && (
      <div className="flex items-center gap-2">
        {selectedGroupId ? (
          <>
            {groupImageUrls[selectedGroupId] ? (
              <img src={groupImageUrls[selectedGroupId]} alt="" className="w-5 h-5 flex-shrink-0 rounded-md object-cover" />
            ) : (
              <GroupIcon className="w-5 h-5 flex-shrink-0 text-eai-ink-faint" />
            )}
            <p className="text-sm font-semibold text-eai-ink">{groups[selectedGroupId]?.name ?? selectedGroupId}</p>
          </>
        ) : (
          <p className="text-xs text-eai-ink-faint">{groupList.length === 0 ? NO_GROUPS_MESSAGE : PICK_GROUP_MESSAGE}</p>
        )}
      </div>
    )}

    {isGroupListOpen && !isSettled && (
      <div className={`overflow-y-auto scrollbar-hover ${groupList.length > 7 ? 'max-h-[252px]' : ''}`}>
        {groupList.length === 0 && (
          <p className="text-xs text-eai-ink-faint py-1.5">{NO_GROUPS_MESSAGE}</p>
        )}
        {groupList.map((g) => (
          <button
            key={g.id}
            type="button"
            onClick={() => {
              setSelectedGroupId(g.id);
              setIsGroupListOpen(false);
            }}
            className={`w-full flex items-center gap-2 py-1.5 text-sm text-left transition-colors hover:text-eai-ink ${g.id === selectedGroupId ? 'font-semibold text-eai-ink' : 'text-eai-ink-muted'}`}
          >
            {groupImageUrls[g.id] ? (
              <img src={groupImageUrls[g.id]} alt="" className="w-5 h-5 flex-shrink-0 rounded-md object-cover" />
            ) : (
              <GroupIcon className="w-5 h-5 flex-shrink-0 text-eai-ink-faint" />
            )}
            <span className="truncate">{g.name}</span>
          </button>
        ))}
      </div>
    )}
  </div>
);

export default GroupSelector;
